// PlayFirst Parent — Settings: edit child name + interests + parent goals,
// plus account sign-out. Heavy edit re-runs the Language Map automatically.
import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Heart, Loader2, LogOut } from 'lucide-react';
import { PFP } from './styles';
import WhatsAppConnectCard from './WhatsAppConnectCard';
import ParentSubscriptionCard from './ParentSubscriptionCard';
import PartnerCodeEntry from './PartnerCodeEntry';
import SponsorCodeCard from './SponsorCodeCard';
import { pickActiveChild } from './childSelection';
import ChildSwitcher from './ChildSwitcher';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function ParentAppSettings() {
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [child, setChild] = useState(null);
  const [form, setForm] = useState({
    name: '', interests: '', parent_goals: '', whatsapp_number: '', date_of_birth: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const applyChild = useCallback((c) => {
    setChild(c);
    setForm({
      name: c.name || '',
      interests: (c.interests || []).join(', '),
      parent_goals: (c.parent_goals || []).join(', '),
      whatsapp_number: c.whatsapp_number || '',
      date_of_birth: c.date_of_birth || '',
    });
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const list = await axios.get(`${API}/parent-app/children`, auth());
        if (!list.data?.length) {
          navigate('/parent-app/onboarding');
          return;
        }
        setChildren(list.data);
        const c = pickActiveChild(list.data);
        applyChild(c);
      } catch (e) {
        toast.error('Could not load settings');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const switchChild = (newId) => {
    const next = children.find((c) => c.id === newId);
    if (!next || next.id === child?.id) return;
    applyChild(next);
  };

  const save = async () => {
    if (!child) return;
    setSaving(true);
    try {
      await axios.put(
        `${API}/parent-app/children/${child.id}`,
        {
          name: form.name.trim(),
          interests: form.interests.split(',').map((s) => s.trim()).filter(Boolean),
          parent_goals: form.parent_goals.split(',').map((s) => s.trim()).filter(Boolean),
          whatsapp_number: form.whatsapp_number.trim(),
          date_of_birth: form.date_of_birth || null,
        },
        auth(),
      );
      // Re-seed the Language Map so the parent sees a fresh, updated version.
      try {
        await axios.post(`${API}/parent-app/children/${child.id}/language-map`, {}, auth());
      } catch (e) { /* non-blocking */ }
      toast.success('Saved');
    } catch (e) {
      toast.error('Could not save');
    } finally {
      setSaving(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/parent-app');
  };

  if (loading) {
    return (
      <div className={PFP.page + ' flex items-center justify-center'}>
        <Loader2 className="w-6 h-6 animate-spin text-[#7C6F9C]" />
      </div>
    );
  }

  return (
    <div className={PFP.page}>
      <header className="px-4 sm:px-8 py-4 max-w-3xl mx-auto flex items-center justify-between">
        <Link to="/parent-app/home" className="flex items-center gap-2 text-stone-600 hover:text-[#7C6F9C]">
          <ArrowLeft className="w-5 h-5" /> Back
        </Link>
        <div className="flex items-center gap-2">
          <Heart className="w-5 h-5 text-[#7C6F9C] fill-[#7C6F9C]/20" />
          <span className="font-serif text-lg text-[#3a2f4f]">Settings</span>
        </div>
        <span />
      </header>

      <main className="max-w-xl mx-auto px-4 py-6 md:py-10 space-y-5">
        {children.length > 1 && (
          <ChildSwitcher items={children} value={child?.id} onChange={switchChild} />
        )}
        <div className={`${PFP.card} p-6 space-y-4`}>
          <h2 className="font-serif text-xl text-[#3a2f4f]">Child profile</h2>
          <Field label="Child's name">
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full h-12 px-4 rounded-2xl border border-stone-200 bg-white focus:border-[#7C6F9C] focus:ring-2 focus:ring-[#7C6F9C]/15 outline-none"
              data-testid="settings-child-name"
            />
          </Field>
          <Field label="Date of birth (so the app stays in sync as they grow)">
            <input
              type="date"
              value={form.date_of_birth}
              onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
              max={new Date().toISOString().slice(0, 10)}
              className="w-full h-12 px-4 rounded-2xl border border-stone-200 bg-white focus:border-[#7C6F9C] focus:ring-2 focus:ring-[#7C6F9C]/15 outline-none"
              data-testid="settings-dob"
            />
            {!form.date_of_birth && (
              <p className="text-[11px] text-amber-700 mt-1.5">
                Adding a birthday lets us auto-update the age band, play ideas and tips as your child grows.
              </p>
            )}
          </Field>
          <Field label="Interests (comma-separated)">
            <input
              type="text"
              value={form.interests}
              onChange={(e) => setForm({ ...form, interests: e.target.value })}
              className="w-full h-12 px-4 rounded-2xl border border-stone-200 bg-white focus:border-[#7C6F9C] focus:ring-2 focus:ring-[#7C6F9C]/15 outline-none"
              data-testid="settings-interests"
            />
          </Field>
          <Field label="What you want support with (comma-separated)">
            <input
              type="text"
              value={form.parent_goals}
              onChange={(e) => setForm({ ...form, parent_goals: e.target.value })}
              className="w-full h-12 px-4 rounded-2xl border border-stone-200 bg-white focus:border-[#7C6F9C] focus:ring-2 focus:ring-[#7C6F9C]/15 outline-none"
              data-testid="settings-goals"
            />
          </Field>
          <Field label="WhatsApp number">
            <input
              type="tel"
              value={form.whatsapp_number}
              onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })}
              className="w-full h-12 px-4 rounded-2xl border border-stone-200 bg-white focus:border-[#7C6F9C] focus:ring-2 focus:ring-[#7C6F9C]/15 outline-none"
              data-testid="settings-whatsapp"
            />
          </Field>
          <button
            onClick={save}
            disabled={saving}
            className={PFP.btnPrimary + ' w-full inline-flex items-center justify-center gap-2'}
            data-testid="settings-save-btn"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {saving ? 'Saving…' : 'Save & refresh Language Map'}
          </button>
        </div>

        <div className={`${PFP.card} p-6`}>
          <h2 className="font-serif text-xl text-[#3a2f4f] mb-2">Account</h2>
          <button
            onClick={logout}
            className="inline-flex items-center gap-2 text-red-600 hover:bg-red-50 px-4 py-2 rounded-full"
            data-testid="settings-signout-btn"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>

        <WhatsAppConnectCard />

        <BloomSettingsCard />

        <ParentSubscriptionCard />

        {/* School/teacher sponsorship redemption */}
        <SponsorCodeCard />

        {/* Partner discount code — for parents who got a code AFTER signing up. */}
        <div className={`${PFP.card} p-6`} data-testid="settings-partner-code-card">
          <h2 className="font-serif text-xl text-[#3a2f4f] mb-2">Partner discount code</h2>
          <p className="text-sm text-stone-600 mb-4">
            Got a code from one of our partners? Add it here to unlock 30% off PlayFirst Parent.
          </p>
          <PartnerCodeEntry redirectOnApply={true} testIdPrefix="settings-partner-code" />
        </div>

        <div className={`${PFP.card} p-6 text-center`}>
          <h2 className="font-serif text-xl text-[#3a2f4f] mb-1">Need a hand?</h2>
          <p className="text-sm text-stone-600 mb-3">
            We&apos;re a small team and we read every message.
          </p>
          <Link
            to="/parent-app/contact"
            className={PFP.btnGhost + ' inline-flex items-center gap-2'}
            data-testid="settings-contact-link"
          >
            Contact &amp; Support
          </Link>
        </div>

        <p className="text-center text-xs text-stone-400">
          PlayFirst Parent is not a diagnostic, screening or emergency tool. If you are
          concerned about your child's communication or development, please speak with a
          qualified health professional.
        </p>
      </main>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm text-stone-600 mb-1">{label}</span>
      {children}
    </label>
  );
}

// Bloom opt-in/out — soft toggle for parents who prefer no engagement
// tracking. Lives in Settings, never auto-prompts.
function BloomSettingsCard() {
  const [optedOut, setOptedOut] = useState(null);
  const [saving, setSaving] = useState(false);
  const authH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/parent-app/bloom`, authH());
        setOptedOut(!!r.data?.opted_out);
      } catch {
        setOptedOut(false);
      }
    })();
  }, []);

  const toggle = async () => {
    const next = !optedOut;
    setSaving(true);
    try {
      await axios.put(`${API}/parent-app/bloom/opt-out`, { opted_out: next }, authH());
      setOptedOut(next);
      toast.success(next ? 'Bloom hidden' : 'Bloom showing — welcome back 💜');
    } catch {
      toast.error("Couldn't update setting");
    } finally {
      setSaving(false);
    }
  };

  if (optedOut === null) return null;
  return (
    <div className={`${PFP.card} p-6`} data-testid="bloom-settings-card">
      <h2 className="font-serif text-xl text-[#3a2f4f] mb-1">Your Bloom</h2>
      <p className="text-sm text-stone-600 mb-3">
        A gentle 30-day view of the days you&apos;ve shown up — no streaks,
        no pressure. We celebrate one-time milestones as they happen.
      </p>
      <button
        type="button"
        onClick={toggle}
        disabled={saving}
        className={PFP.btnGhost + ' inline-flex items-center gap-2'}
        data-testid="bloom-settings-toggle"
      >
        {saving && <Loader2 className="w-4 h-4 animate-spin" />}
        {optedOut ? 'Show my Bloom' : 'Hide my Bloom'}
      </button>
    </div>
  );
}
